import { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, LogOut, Menu, UserCircle2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { useAuth, USER_ROLE_LABELS } from "@/lib/auth";
import { LanguageSwitcher } from "@/components/ui/LanguageSwitcher";
import { useMyProfile, useMySalarie } from "@/lib/data";

interface AppHeaderProps {
  onOpenMobileMenu: () => void;
}

export function AppHeader({ onOpenMobileMenu }: AppHeaderProps) {
  const { t } = useTranslation();
  const { user, role, signOut } = useAuth();
  const { data: profile } = useMyProfile();
  const { data: salarie } = useMySalarie();
  const location = useLocation();

  const greeting = computeGreeting(t);
  // Nom affiché : profil d'abord, puis fiche salarié, e-mail en dernier recours.
  const displayName = profile?.prenom
    ? `${profile.prenom}${profile.nom ? ` ${profile.nom}` : ""}`
    : [salarie?.prenom, salarie?.nom].filter(Boolean).join(" ") || (user?.email ?? "");

  return (
    <header className="sticky top-0 z-20 border-b border-line bg-bg/80 backdrop-blur">
      <div className="flex min-h-16 items-center gap-3 px-4 py-1 lg:px-8">
        {/* Mobile burger */}
        <button
          type="button"
          onClick={onOpenMobileMenu}
          className="focus-ring grid size-9 place-items-center rounded-lg text-ink-muted hover:bg-bg-subtle lg:hidden"
          aria-label="Ouvrir le menu"
        >
          <Menu className="size-5" />
        </button>

        {/* Page title from path (simple breadcrumb) */}
        <Breadcrumb pathname={location.pathname} />

        <div className="ml-auto flex min-w-0 max-w-full items-center gap-3">
          <LanguageSwitcher />
          <div className="hidden min-w-0 max-w-48 text-right [overflow-wrap:anywhere] md:block">
            <div className="text-xs text-ink-subtle">{greeting}</div>
            <div className="text-sm font-medium text-ink">{displayName}</div>
          </div>
          <UserMenu
            avatarName={displayName}
            email={user?.email ?? ""}
            roleLabel={role ? (USER_ROLE_LABELS[role] ?? null) : null}
            onSignOut={() => void signOut()}
          />
        </div>
      </div>
    </header>
  );
}

// -----------------------------------------------------------------------------
// Breadcrumb minimaliste à partir du pathname
// -----------------------------------------------------------------------------
const PATH_LABELS: Record<string, string> = {
  app: "app.nav.dashboard",
  dossier: "app.nav.dossier",
  documents: "app.nav.documents",
  simulations: "app.nav.simulations",
  bri: "app.nav.bri",
  messages: "app.nav.messages",
  rdv: "app.nav.rdv",
  formations: "app.nav.formations",
  profile: "app.nav.profile",
  help: "app.nav.help",
  drh: "app.nav.group_drh",
  aon: "app.nav.group_aon",
  admin: "app.nav.group_admin",
  operations: "app.nav.operations",
  salaries: "app.nav.salaries",
  reporting: "app.nav.reporting",
  dossiers: "app.nav.mes_dossiers",
  calendrier: "app.nav.calendrier",
  bibliotheque: "app.nav.bibliotheque",
  tenants: "app.nav.tenants",
  utilisateurs: "app.nav.utilisateurs",
  branding: "app.nav.branding",
  audit: "app.nav.audit",
  "rachat-bareme": "app.nav.rachat_bareme",
  "services-externes": "app.nav.external_services",
  rgpd: "app.nav.rgpd",
};

function Breadcrumb({ pathname }: { pathname: string }) {
  const { t } = useTranslation();
  const segments = pathname.split("/").filter(Boolean);

  if (segments.length === 0) return null;

  const crumbs = segments.map((seg, index) => {
    const path = "/" + segments.slice(0, index + 1).join("/");
    const labelKey = PATH_LABELS[seg];
    const label = labelKey ? t(labelKey) : decodeURIComponent(seg);
    return { path, label, isLast: index === segments.length - 1 };
  });

  return (
    <nav aria-label="Fil d'Ariane" className="min-w-0 flex-1">
      <ol className="flex items-center gap-1.5 truncate text-sm text-ink-muted">
        {crumbs.map((crumb, index) => (
          <li key={crumb.path} className="flex items-center gap-1.5 truncate">
            {index > 0 && <span className="text-ink-subtle">/</span>}
            {crumb.isLast ? (
              <span className="truncate font-medium text-ink">{crumb.label}</span>
            ) : (
              <Link to={crumb.path} className="truncate hover:text-ink">
                {crumb.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

// -----------------------------------------------------------------------------
// User menu (dropdown)
// -----------------------------------------------------------------------------
interface UserMenuProps {
  avatarName: string;
  email: string;
  roleLabel: string | null;
  onSignOut: () => void;
}

function UserMenu({ avatarName, email, roleLabel, onSignOut }: UserMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handle);
    return () => {
      document.removeEventListener("mousedown", handle);
    };
  }, [open]);

  return (
    <div
      ref={ref}
      className="relative shrink-0"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (open && event.key === "Escape") {
          event.stopPropagation();
          setOpen(false);
          trigger.current?.focus();
        }
      }}
    >
      <button
        type="button"
        ref={trigger}
        onClick={() => setOpen((v) => !v)}
        className="focus-ring group flex items-center gap-2 rounded-full p-1 pr-2 transition-colors hover:bg-bg-subtle"
        aria-controls={open ? menuId : undefined}
        aria-label={`Compte de ${avatarName || email}`}
        aria-expanded={open}
      >
        <UserCircle2 aria-hidden="true" className="size-9 text-ink-muted" />
        <ChevronDown
          className={cn("size-4 text-ink-muted transition-transform", open && "rotate-180")}
        />
      </button>

      {open && (
        <nav
          id={menuId}
          aria-label={`Compte de ${avatarName || email}`}
          className="absolute right-0 top-12 z-30 w-64 max-w-[calc(100vw-2.5rem)] origin-top-right animate-fade-in rounded-xl border border-line bg-surface p-2 shadow-elevated"
        >
          <div className="border-b border-line px-3 py-2.5">
            <div className="truncate text-sm font-semibold text-ink">{avatarName}</div>
            <div className="truncate text-xs text-ink-muted">{email}</div>
            {roleLabel && (
              <div className="mt-1.5 inline-flex items-center rounded-full bg-brand-muted/70 px-2 py-0.5 text-[11px] font-medium text-brand">
                {roleLabel}
              </div>
            )}
          </div>
          <ul className="py-1">
            <li>
              <Link
                to="/app/profile"
                className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-ink hover:bg-bg-subtle"
                onClick={() => setOpen(false)}
              >
                <UserCircle2 className="size-4 text-ink-muted" />
                {t("app.nav.profile")}
              </Link>
            </li>
          </ul>
          <div className="border-t border-line py-1">
            <button
              type="button"
              onClick={onSignOut}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-ink hover:bg-bg-subtle"
            >
              <LogOut className="size-4 text-ink-muted" />
              {t("nav.logout")}
            </button>
          </div>
        </nav>
      )}
    </div>
  );
}

function computeGreeting(t: (key: string) => string): string {
  const hour = new Date().getHours();
  if (hour < 6 || hour >= 22) return t("app.header.greeting_evening");
  if (hour < 12) return t("app.header.greeting_morning");
  if (hour < 18) return t("app.header.greeting_afternoon");
  return t("app.header.greeting_evening");
}
